import { useCallback, useEffect, useRef, useState } from "react";
import { useMessengerNotifications } from "@/contexts/MessengerNotificationsContext";

const PAGE_SIZE = 20;
// En deçà, rouvrir la liste ne redemande rien au serveur.
const FRESH_MS = 15000;

// La première page reste en mémoire d'une ouverture à l'autre : la liste
// s'affiche tout de suite avec ce qu'on connaît, puis se met à jour sans
// repasser par l'écran de chargement.
let cache = null;
let inflight = null;

const fetchPage = async (loaded) => {
    const response = await fetch("/api/messenger/get/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            messagesToLoad: PAGE_SIZE,
            messagesLoaded: loaded,
            onlyWaiting: false,
            onlyGuides: false,
        }),
    });
    if (!response.ok) throw new Error("messenger list failed");
    return response.json();
};

const invalidate = () => {
    if (cache) cache = { ...cache, at: 0 };
};

/**
 * Charge la première page si elle manque ou a vieilli. Appelée au survol de
 * la bulle, pour que la liste soit là au moment du clic.
 */
export const prefetchQuickDiscussions = () => {
    if (cache && Date.now() - cache.at < FRESH_MS) return Promise.resolve(cache);
    if (inflight) return inflight;
    inflight = fetchPage(0)
        .then((data) => {
            cache = {
                messages: data.messages || [],
                more: !!data.more,
                at: Date.now(),
            };
            return cache;
        })
        // Sans réseau, on garde ce qu'on avait.
        .catch(() => cache)
        .finally(() => {
            inflight = null;
        });
    return inflight;
};

export const useQuickDiscussions = () => {
    const { subscribe } = useMessengerNotifications();
    const [state, setState] = useState(cache);
    const [settled, setSettled] = useState(Boolean(cache));
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const aliveRef = useRef(true);

    const refresh = useCallback(async () => {
        const next = await prefetchQuickDiscussions();
        if (!aliveRef.current) return;
        if (next) setState(next);
        setSettled(true);
    }, []);

    useEffect(() => {
        aliveRef.current = true;
        refresh();
        // Un message qui arrive pendant que la liste est ouverte la remet à
        // jour : nouvel aperçu, discussion remontée en tête.
        const unsubscribe = subscribe("messengerNewMessage", () => {
            invalidate();
            refresh();
        });
        return () => {
            aliveRef.current = false;
            unsubscribe();
        };
    }, [refresh, subscribe]);

    const loadMore = useCallback(async () => {
        if (!state?.more || isLoadingMore) return;
        setIsLoadingMore(true);
        try {
            const data = await fetchPage(state.messages.length);
            const known = new Set(state.messages.map((message) => message.id));
            const merged = [
                ...state.messages,
                ...(data.messages || []).filter((message) => !known.has(message.id)),
            ];
            // Les pages suivantes ne sont pas gardées : à la prochaine
            // ouverture, la liste repart de la première, à jour.
            if (aliveRef.current) {
                setState({ messages: merged, more: !!data.more, at: Date.now() });
            }
        } catch {
            // La sentinelle redemandera au prochain défilement.
        } finally {
            if (aliveRef.current) setIsLoadingMore(false);
        }
    }, [state, isLoadingMore]);

    return {
        messages: state?.messages || [],
        isLoading: !settled,
        isLoadingMore,
        loadMore,
    };
};
